import type { ChatRevertItem } from "schemas/generated/chatMessage";
import type { FileVersion } from "schemas/generated/fileVersion";

import React from "react";

import { useIsDebugMode } from "@editor/components/editor/debug/useIsDebugMode";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { chatRouter, chatUtils } from "@editor/trpc/chat";
import { fileVersionRouter, fileVersionUtils } from "@editor/trpc/fileVersion";
import { nextJSProjectRouter } from "@editor/trpc/nextJSProject";

import { useUpdatePageFilesInWebContainer } from "@/features/code-execution/web-container/useUpdatePageFilesInWebContainer";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import Popover from "@replo/design-system/components/popover/Popover";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { MoreHorizontal, X } from "lucide-react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useSearchParams } from "react-router-dom";
import { formatDateToMonthDayHour } from "replo-utils/lib/datetime";

import { useEditorStoreActions, usePanelView } from "../../store/editorStore";
import {
  useSandboxStateActions,
  useViewingVersionId,
} from "../../store/SandboxStateStoreContext";

const INITIAL_ITEMS_TO_LOAD = 20;

export const VersionCard = ({
  version,
  isViewingVersion,
  isCurrentVersion,
  appliedDiffs,
}: {
  version: FileVersion;
  isViewingVersion: boolean;
  isCurrentVersion: boolean;
  appliedDiffs?: string[];
}) => {
  const projectId = useCurrentProjectId();
  const { user } = useCurrentUser();
  const isDebug = useIsDebugMode();
  const panelView = usePanelView();
  const logEvent = useLogAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageId = searchParams.get("pageId");
  const chatId = searchParams.get("chatId");
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const { setViewingVersionId } = useSandboxStateActions();
  const isServerSandboxEnabled = isFeatureEnabled("use-server-sandbox");

  const updatePageFilesInWebContainer = useUpdatePageFilesInWebContainer();

  const {
    mutateAsync: revertPageToVersion,
    isPending: isRevertingPageToVersion,
  } = nextJSProjectRouter.revertPageToVersion.useMutation({
    onSuccess: () => {
      if (!isServerSandboxEnabled) {
        void updatePageFilesInWebContainer({
          pages: [{ id: pageId! }],
        });
      }
    },
  });

  const { data: file } = nextJSProjectRouter.getPage.useQuery(
    pageId && projectId
      ? {
          projectId,
          id: version.fileId,
        }
      : skipToken,
  );

  const { mutate: addMessage } = chatRouter.addMessage.useMutation({
    onSuccess: () => {
      if (chatId) {
        void chatUtils.get.invalidate({
          chatId,
          projectId,
        });
      }
      void fileVersionUtils.findByFileId.invalidate({
        projectId,
        fileId: pageId!,
      });
      void chatUtils.getPaginatedMessages.invalidate({
        chatId: chatId!,
        projectId,
      });
    },
  });

  const { mutateAsync: createChat } = chatRouter.create.useMutation();

  const handleRevertPageToVersion = React.useCallback(async () => {
    if (projectId && user?.id) {
      const newVersion = await revertPageToVersion({
        projectId,
        fileVersionId: version.id,
        userId: user?.id,
      });

      logEvent("page.restore", {
        origin: panelView === "chat" ? "chat" : "version-history",
      });

      let chatIdToUse = chatId;

      if (!chatId) {
        const newChat = await createChat({
          projectId,
        });

        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("chatId", newChat.id);
          return newParams;
        });
        chatIdToUse = newChat.id;
      }

      if (chatIdToUse) {
        const revertItem: ChatRevertItem = {
          fileVersionId: newVersion.id,
          context: "User reverted to this version through the UI",
        };
        addMessage({
          projectId,
          chatId: chatIdToUse,
          content: [
            {
              type: "text",
              contentType: "revert" as const,
              text: JSON.stringify(revertItem),
              isNewMessageGroup: true,
              fileId: pageId ?? undefined,
            },
          ],
          role: "assistant",
        });
      }

      setViewingVersionId(newVersion.id);
    }
  }, [
    projectId,
    version.id,
    user?.id,
    revertPageToVersion,
    chatId,
    addMessage,
    setViewingVersionId,
    createChat,
    setSearchParams,
    pageId,
    logEvent,
    panelView,
  ]);

  const handlePreviewVersion = React.useCallback(async () => {
    if (!projectId || !pageId || !version.id) {
      return;
    }

    try {
      setPreviewLoading(true);
      await updatePageFilesInWebContainer({
        pages: [{ id: pageId!, fileVersionId: version.id }],
      });
      setViewingVersionId(version.id);
      logEvent("page.preview", {
        origin: panelView === "chat" ? "chat" : "version-history",
      });
    } finally {
      setPreviewLoading(false);
    }
  }, [
    projectId,
    pageId,
    version.id,
    setViewingVersionId,
    updatePageFilesInWebContainer,
    logEvent,
    panelView,
  ]);

  const handleOpenPage = React.useCallback(
    async (pageId: string, fileVersionId: string) => {
      try {
        setPreviewLoading(true);
        await updatePageFilesInWebContainer({
          pages: [{ id: pageId!, fileVersionId: fileVersionId }],
        });
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("pageId", pageId);
          return newParams;
        });
        setViewingVersionId(fileVersionId);
      } finally {
        setPreviewLoading(false);
      }
    },
    [setSearchParams, setViewingVersionId, updatePageFilesInWebContainer],
  );

  return (
    <div
      className={twMerge(
        "flex flex-col p-4 rounded-xl mr-3",
        isViewingVersion ? "border-2 border-default" : "border border-border",
      )}
    >
      <div className="text-base font-medium mb-1 truncate">
        {version.summary}
      </div>
      {version.fileId === pageId ? (
        <div className="text-sm mb-1 text-muted">
          {isCurrentVersion
            ? "Current Version"
            : `${formatDateToMonthDayHour(version.createdAt)}`}
        </div>
      ) : (
        <div className="text-sm mb-1 text-muted">{file?.pageRoute}</div>
      )}

      <div className="flex flex-row w-full gap-2 justify-end">
        {isDebug && appliedDiffs && appliedDiffs.length > 0 && (
          <Popover
            triggerAsChild
            shouldPreventDefaultOnInteractOutside={false}
            side="bottom"
            align="start"
            sideOffset={8}
            className="w-[500px]"
            title="Debug Changes"
            content={
              <div className="max-h-96 overflow-auto flex flex-col gap-2">
                <div className="text-sm">File version: {version.id}</div>
                <div className="text-sm">File version URL: {version.url}</div>
                <p className="text-sm">
                  Note that if biome auto-fixed issues they won't show up in the
                  diffs here. This shows all diffs, including the original
                  change and any diffs that the cleanup tool generated.
                </p>
                <div className="flex flex-col gap-1">
                  {appliedDiffs.map((diff, index) => (
                    <pre
                      key={index}
                      className="text-xs bg-neutral-soft p-2 rounded border mb-2 whitespace-pre-wrap"
                    >
                      {diff}
                    </pre>
                  ))}
                </div>
              </div>
            }
          >
            <IconButton
              variant="secondary"
              icon={<MoreHorizontal className="size-4" />}
              tooltipText="View debug diffs"
              size="sm"
            />
          </Popover>
        )}
        {version.fileId === pageId ? (
          <>
            {!isCurrentVersion && (
              <Button
                onClick={() => {
                  void handleRevertPageToVersion();
                }}
                isLoading={isRevertingPageToVersion}
                variant="tertiary"
              >
                Restore
              </Button>
            )}
            <Button
              disabled={isViewingVersion}
              onClick={() => {
                void handlePreviewVersion();
              }}
              isLoading={previewLoading}
              variant="secondary"
            >
              {isViewingVersion ? "Viewing" : "Preview"}
            </Button>
          </>
        ) : (
          <Button
            onClick={() => {
              void handleOpenPage(version.fileId, version.id);
            }}
            isLoading={previewLoading}
            variant="secondary"
          >
            Open Page
          </Button>
        )}
      </div>
    </div>
  );
};

function VersionHistory() {
  const [searchParams] = useSearchParams();
  const pageId = searchParams.get("pageId");
  const viewingVersionId = useViewingVersionId();
  const projectId = useCurrentProjectId();
  const { setPanelView } = useEditorStoreActions();

  const {
    data: fileVersionsData,
    fetchNextPage: fetchNextVersionsPage,
    hasNextPage: hasNextVersionsPage,
  } = fileVersionRouter.findByFileId.useInfiniteQuery(
    projectId && pageId
      ? {
          projectId,
          fileId: pageId,
          limit: INITIAL_ITEMS_TO_LOAD,
          groupBy: {
            changeType: "MODIFIER",
          },
        }
      : skipToken,
    {
      getNextPageParam: ({ nextCursor }) => nextCursor,
      staleTime: 0,
    },
  );

  const fileVersions = React.useMemo(
    () => fileVersionsData?.pages?.flatMap((page) => page.fileVersions) ?? [],
    [fileVersionsData],
  );

  const currentVersion = fileVersions?.[0];

  return (
    <div className="flex flex-col w-full h-full shadow-selectable border-0.5 border-border rounded-lg bg-white relative overflow-hidden">
      <div className="flex flex-row justify-between items-center border-b-0.5 border-border p-4">
        <div className="header-h3">Version History</div>
        <IconButton
          icon={<X className="w-4 h-4" />}
          onClick={() => setPanelView("chat")}
          variant="tertiary"
          size="sm"
        />
      </div>
      <div
        className="flex flex-col gap-2 overflow-y-auto flex-1 p-4"
        id="versions-scroll-container"
      >
        <InfiniteScroll
          dataLength={fileVersions.length}
          next={() => void fetchNextVersionsPage()}
          hasMore={hasNextVersionsPage}
          scrollableTarget="versions-scroll-container"
          loader={
            <div className="text-center p-2 flex justify-center items-center">
              <Spinner variant="primary" size={16} />
            </div>
          }
        >
          <div className="flex flex-col gap-3">
            {fileVersions?.map((version) => (
              <VersionCard
                key={version.id}
                isViewingVersion={viewingVersionId === version.id}
                isCurrentVersion={version.id === currentVersion?.id}
                version={version}
              />
            ))}
          </div>
        </InfiniteScroll>
      </div>
    </div>
  );
}

export default VersionHistory;
